<script setup lang="ts">
// SettingsNavTree — the rail's settings mode (contract 05 §10, the `SideNavItems.vue:177–256`
// half of the legacy split; contract 22 CM-22-04). It renders module 22's `useSettingsNav()`
// read model and nothing else: 22 owns which entries exist and whether each is visible, five
// other modules own the entries themselves, and this component owns only the chrome.
//
// It is NOT a second nav engine. The entries are mapped to the shell's typed `NavItem` and run
// through `buildNavTree` — the same prune-and-mark pass the main tree uses — with the same
// `createNavVisibility(createDefaultNavVisibilitySources())` sources, and rendered by the same
// recursive `NavTreeItem` (which resolves labels through `useNavLabels`). A rule the engine hides
// in the main tree is hidden here for the identical reason, and `current` is decided once.
//
// ⚠ contract 05 §10 used to describe this component as "driven by `navStore.selectedSettings`"
// (and to sketch a separate `SettingsNav` panel beside it). That was the pre-22 shape: CM-22-04
// replaced main/sub tab state with the URL, so there is no tab for the shell to hold and no second
// panel to toggle. Both rows were corrected in ORP-19896 — see §10's amendment note. The tab state
// itself is gone as of ORP-19898; `navStore` now holds only `showSettingsNav` + `previousRoute`.
import { computed } from 'vue'
import { useRoute, useRouter, type LocationQueryRaw, type RouteParamsRawGeneric } from 'vue-router'

import NavTreeItem from './NavTreeItem.vue'

import {
  buildNavTree,
  createDefaultNavVisibilitySources,
  createNavVisibility,
  useNavStore,
  type BuiltNavNode,
  type NavBuildContext,
  type NavItem,
} from '@/app/navigation'
import { HOME_ROUTE_NAME } from '@/app/router/routeNames'
import {
  useSettingsNav,
  useSettingsRbac,
  type SettingsNavEntry,
} from '@/features/workspace-settings'
import { useI18n } from '@/shared/i18n'
import { Icon } from '@/shared/ui/icon'

withDefaults(defineProps<{ expanded?: boolean }>(), { expanded: false })
const emit = defineEmits<{ navigate: [item: BuiltNavNode] }>()

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const navStore = useNavStore()
const { groups } = useSettingsNav()
// The SAME gate `SettingsIndex.vue` redirects on. Reading it here is what keeps the rail and the
// pane from disagreeing: a user who gets the "settings are not available" state must not also be
// looking at a full settings tree (contract 22 §14).
const { canOpenSettings } = useSettingsRbac()

const ctx: NavBuildContext = {
  visibility: createNavVisibility(createDefaultNavVisibilitySources()),
  // Settings entries are never report-backed (`SettingsNavEntry` carries a route name, not a
  // report id), so `buildNavNode` never reaches for a report key.
  // eslint-disable-next-line unicorn/no-useless-undefined -- `NavBuildContext` requires the member; "no report key, ever" is the honest answer
  reportKeyOf: () => undefined,
}

/**
 * One registered entry → the shell's nav model. `navKey` is the entry key, which is exactly what
 * 22 puts on each pane route's `meta.navKey` — so the shared engine marks `current` by the rule it
 * already has, and 22's own `isActive()` never needs a second implementation here.
 *
 * `badge` and `parent` are deliberately not mapped: `NavItem.badgeKey` is an i18n KEY while 22's
 * `badge()` returns a value, and no registrant uses either field yet. Mapping them would be
 * inventing a rendering contract for zero producers.
 */
function toNavItem(entry: SettingsNavEntry): NavItem {
  return {
    labelKey: entry.labelKey,
    icon: entry.icon,
    navKey: entry.key,
    to: { name: entry.routeName, ...(entry.params ? { params: entry.params } : {}) },
  }
}

/**
 * New Front renders Workspace and Account as expandable disclosures, followed by the remaining
 * settings destinations as siblings. The projection remains registry-driven: predicates and order
 * are resolved by `useSettingsNav()`, grouped children stay in their registered order, and no entry
 * key or route is repeated here.
 */
const items = computed(() => {
  if (!canOpenSettings.value) return []

  const tree: NavItem[] = []
  for (const group of groups.value) {
    if (group.key === 'workspace' || group.key === 'account') {
      tree.push({
        labelKey: group.labelKey,
        icon: group.key === 'workspace' ? 'globe' : 'clipboard-list',
        children: group.entries.map(toNavItem),
      })
      continue
    }
    tree.push(...group.entries.map(toNavItem))
  }
  return buildNavTree(tree, route, ctx)
})

/** v3 `handleBackToSummary` (`SideNavItems.vue:384–391`): return to where settings was opened
 *  from, falling back to home when there is nowhere to go back to. The takeover itself closes off
 *  the route (`navStore.syncOnNavigation`), so this pushes and lets the afterEach do the rest —
 *  a failed push leaves the user in settings with the nav intact rather than in a blank shell. */
async function goBack(): Promise<void> {
  const previous = navStore.previousRoute
  try {
    await router.push(
      previous
        ? {
            name: previous.name,
            // The snapshot is spread verbatim from a `RouteLocationNormalized`, so these ARE route
            // params/query; contract 05 §5 pins `PreviousRoute` as `Record<string, unknown>`, which
            // vue-router's raw-location types cannot see through.
            params: previous.params as RouteParamsRawGeneric,
            query: previous.query as LocationQueryRaw,
          }
        : { name: HOME_ROUTE_NAME },
    )
  } catch {
    /* target route no longer registered — stay put (v3 caught and logged the same case) */
  }
}
</script>

<template>
  <div class="settings-nav flex h-full min-h-0 flex-col gap-md" data-testid="settings-nav-tree">
    <button
      type="button"
      class="settings-nav__back mx-[calc(var(--ui-space-2xs)/2)] flex min-h-[3.3125rem] w-[calc(100%-var(--ui-space-2xs))] flex-none cursor-pointer items-center gap-sm whitespace-nowrap rounded-none border-0 border-b border-border bg-transparent px-md pt-xs pb-md text-[0.9375rem] font-semibold leading-normal text-fg transition-[width,color] duration-300 hover:text-primary motion-reduce:transition-none"
      :class="{ 'settings-nav__back--expanded': expanded }"
      :aria-label="t('shell.nav.settingsBack')"
      :title="t('shell.nav.settingsBack')"
      data-testid="settings-nav-back"
      @click="goBack"
    >
      <span
        class="settings-nav__back-icon grid h-4 w-4 flex-[0_0_1rem] rotate-90 place-items-center"
        aria-hidden="true"
      >
        <Icon name="chevron-down" :size="16" />
      </span>
      <span
        v-if="expanded"
        class="settings-nav__back-label min-w-0 overflow-hidden text-ellipsis text-base font-medium"
      >
        {{ t('shell.nav.settings') }}
      </span>
    </button>

    <ul
      v-if="items.length"
      class="settings-nav__tree grid min-h-0 w-full flex-auto auto-rows-min content-start grid-cols-[minmax(0,1fr)] gap-0 m-0 list-none overflow-y-auto p-0"
    >
      <NavTreeItem
        v-for="item in items"
        :key="item.navKey ?? item.labelKey ?? item.to?.name ?? 'settings-nav-item'"
        :item="item"
        :expanded="expanded"
        @navigate="emit('navigate', $event)"
      />
    </ul>
  </div>
</template>

<style scoped>
.settings-nav
  :deep(
    .nav-tree-item:not(.nav-tree-item--nested)
      > .nav-tree-item__group
      > .nav-tree-item__summary.nav-tree-item__control--current
  ) {
  background-color: var(--ui-color-surface-sunken);
  box-shadow: var(--ui-shadow-input);
}
.settings-nav
  :deep(
    .nav-tree-item:not(.nav-tree-item--nested)
      > .nav-tree-item__link.nav-tree-item__control--current
  ) {
  background-color: var(--ui-color-surface-sunken);
  box-shadow: var(--ui-shadow-input);
}
.settings-nav :deep(.nav-tree-item--nested .nav-tree-item__link) {
  min-height: 2.5rem;
}
.settings-nav :deep(.nav-tree-item--nested .nav-tree-item__summary) {
  min-height: 2.5rem;
}
.settings-nav :deep(.nav-tree-item__group > .nav-tree-item__children) {
  gap: 0;
}
.settings-nav
  .settings-nav__tree
  > :deep(
    .nav-tree-item
      > .nav-tree-item__group
      > .nav-tree-item__summary.nav-tree-item__control--expanded
  ) {
  min-height: 2.75rem;
  width: 100%;
  padding-inline-end: var(--ui-space-md);
}
.settings-nav
  .settings-nav__tree
  > :deep(.nav-tree-item > .nav-tree-item__link.nav-tree-item__control--expanded) {
  min-height: 2.75rem;
  width: 100%;
  padding-inline-end: var(--ui-space-md);
}
</style>
